import { useEffect, useState } from 'react'
import {
  Bot,
  Loader2,
  Sparkles,
  Activity,
  UserCircle,
  FileText,
} from 'lucide-react'

import { Card, CardContent } from '@/ui/card'
import { cn } from '@/core/lib/utils'
import { useToast } from '@/core/hooks/useToast'
import { useIsMounted } from '@/core/hooks/useIsMounted'

import { useAgentStore } from './stores/agentStore'
import { SKILL_LABELS, type AgentConfig, type AgentSkills } from './api/agentService'
import { LEVELS, getRole, getSystemPrompt, getSkills } from './lib/config'
import { AgentOverviewTab } from './components/AgentOverviewTab'
import { AgentPersonalityTab } from './components/AgentPersonalityTab'
import { AgentHistoryTab } from './components/AgentHistoryTab'

const TABS = [
  { key: 'overview' as const, label: 'Overview', icon: Activity },
  { key: 'personality' as const, label: 'Personality & Role', icon: UserCircle },
  { key: 'history' as const, label: 'Action History', icon: FileText },
]

export function AgentSettingsPage() {
  const mounted = useIsMounted()
  const { configs, isLoading, error, loadConfigs, updateConfig, runs, runsLoading, runsError, loadRunsByAgent } = useAgentStore()
  const { show, ToastUI } = useToast()

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'personality' | 'history'>('overview')
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [savingPersonality, setSavingPersonality] = useState(false)

  // Local form state for personality
  const [roleDraft, setRoleDraft] = useState('')
  const [systemPromptDraft, setSystemPromptDraft] = useState('')

  useEffect(() => {
    void (async () => {
      if (!mounted.current) {return}
      await loadConfigs()
    })()
  }, [loadConfigs])

  // Auto-select first agent when loaded
  useEffect(() => {
    if (configs.length > 0 && !selectedAgentId) {
      setSelectedAgentId(configs[0].agentId)
    }
  }, [configs, selectedAgentId])

  // Load runs when switching to history tab or agent
  useEffect(() => {
    if (activeTab !== 'history' || !selectedAgentId) {return}
    void (async () => {
      if (!mounted.current) {return}
      await loadRunsByAgent(selectedAgentId)
    })()
  }, [activeTab, selectedAgentId, loadRunsByAgent])

  // Sync drafts when agent changes
  useEffect(() => {
    const cfg = configs.find((c) => c.agentId === selectedAgentId)
    if (cfg) {
      setRoleDraft(getRole(cfg.config))
      setSystemPromptDraft(getSystemPrompt(cfg.config))
    }
  }, [selectedAgentId, configs])

  const selectedAgent = configs.find((c) => c.agentId === selectedAgentId)

  const handleToggle = async (cfg: AgentConfig) => {
    setUpdatingId(cfg.agentId)
    try {
      await updateConfig(cfg.agentId, { enabled: !cfg.enabled })
      show(!cfg.enabled ? `${cfg.name} activated` : `${cfg.name} deactivated`)
    } catch {
      show('Failed to update agent', 'error')
    } finally {
      setUpdatingId(null)
    }
  }

  const handleLevelChange = async (cfg: AgentConfig, level: AgentConfig['autonomyLevel']) => {
    if (cfg.autonomyLevel === level) {
      return
    }
    setUpdatingId(cfg.agentId)
    try {
      await updateConfig(cfg.agentId, { autonomyLevel: level })
      const label = LEVELS.find((l) => l.value === level)?.label ?? level
      show(`${cfg.name} set to "${label}"`)
    } catch {
      show('Failed to update autonomy level', 'error')
    } finally {
      setUpdatingId(null)
    }
  }

  const handleSkillToggle = async (cfg: AgentConfig, skillKey: keyof AgentSkills) => {
    const current = getSkills(cfg.config)
    const next = { ...current, [skillKey]: !current[skillKey] }
    const nextConfig = { ...cfg.config, skills: next }
    setUpdatingId(cfg.agentId)
    try {
      await updateConfig(cfg.agentId, { config: nextConfig })
      show(`${SKILL_LABELS[skillKey].label} ${next[skillKey] ? 'enabled' : 'disabled'}`)
    } catch {
      show('Failed to update skill', 'error')
    } finally {
      setUpdatingId(null)
    }
  }

  const handleSavePersonality = async (cfg: AgentConfig) => {
    setSavingPersonality(true)
    try {
      const nextConfig = { ...cfg.config, role: roleDraft.trim(), system_prompt: systemPromptDraft.trim() }
      await updateConfig(cfg.agentId, { config: nextConfig })
      show('Personality & role saved')
    } catch {
      show('Failed to save personality', 'error')
    } finally {
      setSavingPersonality(false)
    }
  }

  const activeCount = configs.filter((c) => c.enabled).length

  return (
    <div className="flex flex-col lg:flex-row gap-5 h-[calc(100vh-7rem)]">
      {/* ─── Agent sidebar ─── */}
      <aside className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-navy">AI Agents</h2>
          </div>
          <p className="text-xs text-slate-500">
            {activeCount} of {configs.length} active
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 py-4">{error}</div>
        ) : (
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {configs.map((cfg) => {
              const isSelected = cfg.agentId === selectedAgentId
              const lvl = LEVELS.find((l) => l.value === cfg.autonomyLevel) ?? LEVELS[0]
              return (
                <button
                  key={cfg.agentId}
                  onClick={() => { setSelectedAgentId(cfg.agentId); setActiveTab('overview') }}
                  className={cn(
                    'flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all min-w-[200px] lg:min-w-0',
                    isSelected
                      ? 'bg-orange-50 border-primary shadow-sm'
                      : 'bg-white border-slate-150 hover:border-primary/50'
                  )}
                >
                  <div
                    className={cn(
                      'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                      cfg.enabled ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-300'
                    )}
                  >
                    <Bot className="w-[1.125rem] h-[1.125rem]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate text-navy">
                      {cfg.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          cfg.enabled ? 'bg-green-500' : 'bg-gray-300'
                        )}
                      />
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                        {cfg.enabled ? lvl.shortLabel : 'Off'}
                      </span>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </aside>

      {/* ─── Main content ─── */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        {!selectedAgent ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Bot className="w-12 h-12 text-slate-150 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Select an agent to configure</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Agent header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border border-slate-150 p-4">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    selectedAgent.enabled ? 'bg-orange-50 text-primary' : 'bg-gray-100 text-gray-300'
                  )}
                >
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy">{selectedAgent.name}</h3>
                  <p className="text-xs text-slate-400">{selectedAgent.description || 'No description'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggle(selectedAgent)}
                  disabled={updatingId === selectedAgent.agentId}
                  className={cn(
                    'relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50',
                    selectedAgent.enabled ? 'bg-primary' : 'bg-gray-200'
                  )}
                >
                  {updatingId === selectedAgent.agentId && (
                    <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />
                  )}
                  <span
                    className={cn(
                      'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300',
                      selectedAgent.enabled ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
                <span className={cn('text-sm font-medium', selectedAgent.enabled ? 'text-green-600' : 'text-gray-400')}>
                  {selectedAgent.enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-150 p-1 w-fit">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    activeTab === t.key
                      ? 'bg-navy text-white'
                      : 'text-slate-500 hover:text-navy hover:bg-gray-50'
                  )}
                >
                  <t.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{t.label}</span>
                </button>
              ))}
            </div>

            {/* Tab content */}
            {activeTab === 'overview' && (
              <AgentOverviewTab
                agent={selectedAgent}
                updatingId={updatingId}
                onLevelChange={handleLevelChange}
                onSkillToggle={handleSkillToggle}
              />
            )}

            {activeTab === 'personality' && (
              <AgentPersonalityTab
                roleDraft={roleDraft}
                systemPromptDraft={systemPromptDraft}
                onRoleChange={setRoleDraft}
                onPromptChange={setSystemPromptDraft}
                onSave={() => handleSavePersonality(selectedAgent)}
                saving={savingPersonality}
              />
            )}

            {activeTab === 'history' && (
              <AgentHistoryTab runs={runs} loading={runsLoading} error={runsError} />
            )}
          </div>
        )}
      </main>

      {ToastUI}
    </div>
  )
}
